import { Injectable, inject } from '@angular/core';
import { Observable, Subscription, from, map, switchMap } from 'rxjs';

// Modern, tree-shakable Firebase Storage
import { Storage, ref, uploadBytes, getDownloadURL, uploadBytesResumable } from '@angular/fire/storage';

export interface UploadProgress {
  progress: number;
  url?: string;
}

export interface ItemImageUploadProgress extends UploadProgress {
  thumbnailUrl?: string;
  thumbnailFailed?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseStorageService {
  // Use the modern `inject` function for dependency injection
  private storage: Storage = inject(Storage);

  /**
   * Uploads a file to a given path in Firebase Storage and returns an observable with the public download URL.
   * @param file The file to upload.
   * @param path The storage path (e.g., 'return-jobs/123/documents').
   * @returns An observable that emits the public URL string upon completion.
   */
  uploadFileAndGetPublicUrl(file: File, path: string): Observable<string> {
    // 1. Create a reference to the storage location
    const filePath = `${path}/${new Date().getTime()}_${file.name}`;
    const fileRef = ref(this.storage, filePath);

    // 2. Start the upload, which returns a Promise. Convert it to an Observable.
    const uploadTask$ = from(uploadBytes(fileRef, file));

    // 3. Chain the operations with RxJS's switchMap operator
    return uploadTask$.pipe(
      // After the upload is complete, switch to a new observable: the download URL
      switchMap(() => from(getDownloadURL(fileRef)))
    );
  }

  uploadFileAndGetStorageUri(file: File, filePath: string, contentType: string): Observable<string> {
    const fileRef = ref(this.storage, filePath);
    return from(uploadBytes(fileRef, file, { contentType })).pipe(map(snapshot => snapshot.ref.toString()));
  }

  /**
   * Uploads a file to a given path in Firebase Storage, reporting progress and returning the final URL.
   * @param file The file to upload.
   * @param path The storage path (e.g., 'drivers/residence_scan').
   * @returns An observable that emits UploadProgress objects.
   */
  uploadFileWithProgress(file: File, path: string): Observable<UploadProgress> {
    const filePath = `${path}/${new Date().getTime()}_${file.name}`;
    const fileRef = ref(this.storage, filePath);
    const uploadTask = uploadBytesResumable(fileRef, file);

    return new Observable<UploadProgress>(observer => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next({ progress: Math.round(progress) });
        },
        (error) => {
          observer.error(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
            observer.next({ progress: 100, url: downloadURL });
            observer.complete();
          }).catch(error => observer.error(error));
        }
      );
    });
  }

  uploadFileToExactPathWithProgress(file: File, filePath: string): Observable<UploadProgress> {
    const fileRef = ref(this.storage, filePath);
    const uploadTask = uploadBytesResumable(fileRef, file);

    return new Observable<UploadProgress>(observer => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          observer.next({ progress: Math.round(progress) });
        },
        (error) => {
          observer.error(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
            observer.next({ progress: 100, url: downloadURL });
            observer.complete();
          }).catch(error => observer.error(error));
        }
      );
    });
  }

  /**
   * Uploads the original image and a tiny stored preview. A thumbnail failure
   * is deliberately non-fatal: callers still receive the original URL and may
   * save the item, while the directory safely renders a placeholder.
   */
  uploadItemImageWithThumbnail(file: File, path: string): Observable<ItemImageUploadProgress> {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileKey = `${Date.now()}_${safeName}`;
    const originalPath = `${path}/${fileKey}`;

    return new Observable<ItemImageUploadProgress>(observer => {
      let thumbnailSubscription: Subscription | undefined;
      const originalSubscription = this.uploadFileToExactPathWithProgress(file, originalPath).subscribe({
        next: original => {
          if (!original.url) {
            observer.next({ progress: Math.round(original.progress * 0.85) });
            return;
          }

          this.createThumbnailFile(file).then(thumbnail => {
            const thumbnailPath = `${path}/thumbnails/${fileKey.replace(/\.[^.]+$/, '')}.${thumbnail.type === 'image/webp' ? 'webp' : 'jpg'}`;
            thumbnailSubscription = this.uploadFileToExactPathWithProgress(thumbnail, thumbnailPath).subscribe({
              next: uploadedThumbnail => {
                if (uploadedThumbnail.url) {
                  observer.next({ progress: 100, url: original.url, thumbnailUrl: uploadedThumbnail.url });
                  observer.complete();
                } else {
                  observer.next({ progress: 85 + Math.round(uploadedThumbnail.progress * 0.15) });
                }
              },
              error: () => {
                observer.next({ progress: 100, url: original.url, thumbnailFailed: true });
                observer.complete();
              }
            });
          }).catch(() => {
            observer.next({ progress: 100, url: original.url, thumbnailFailed: true });
            observer.complete();
          });
        },
        error: error => observer.error(error)
      });

      return () => {
        originalSubscription.unsubscribe();
        thumbnailSubscription?.unsubscribe();
      };
    });
  }

  private async createThumbnailFile(file: File): Promise<File> {
    const image = await createImageBitmap(file);
    try {
      const scale = Math.max(96 / image.width, 96 / image.height);
      const width = Math.max(1, Math.ceil(image.width * scale));
      const height = Math.max(1, Math.ceil(image.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = 96;
      canvas.height = 96;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Thumbnail canvas is unavailable');
      }
      context.drawImage(image, Math.round((96 - width) / 2), Math.round((96 - height) / 2), width, height);

      let blob = await this.canvasToBlob(canvas, 'image/webp', 0.80);
      if (blob.type !== 'image/webp') {
        blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.80);
      }
      const extension = blob.type === 'image/webp' ? 'webp' : 'jpg';
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'item-image';
      return new File([blob], `${baseName}.${extension}`, { type: blob.type });
    } finally {
      image.close();
    }
  }

  private canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => canvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Thumbnail encoding failed'));
      }
    }, type, quality));
  }
}
